/**
 * QuoteDetailWorkspace — mock-aligned tabbed quote cockpit (Sprint 8.9).
 */

import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import QuoteCustomerOfferCard from "./QuoteCustomerOfferCard.jsx";
import QuoteCustomerLineItems from "./QuoteCustomerLineItems.jsx";
import QuoteInternalSummary from "./QuoteInternalSummary.jsx";
import QuoteCustomerPreview from "./QuoteCustomerPreview.jsx";
import QuoteTimeline from "./QuoteTimeline.jsx";
import QuoteNextActionChip from "./QuoteNextActionChip.jsx";
import QuoteAutomatedPreparationReviewPanel from "./QuoteAutomatedPreparationReviewPanel.jsx";
import QuoteFinanceStrip from "./QuoteFinanceStrip.jsx";
import QuoteDiagnosticsPanel from "./QuoteDiagnosticsPanel.jsx";

const TabPanel = ({ value, index, children }) => {
  if (value !== index) return null;
  return <Box pt={2.5}>{children}</Box>;
};

const QuoteDetailWorkspace = ({
  quote,
  linkedLead = null,
  onOpenLead,
  onPreviewPdf,
  pdfReady = false,
  pdfBusy = false,
  customerNotesSlot = null,
  opsToolsSlot = null,
  onPreparationApproved = null,
  onRevisionCreated = null,
}) => {
  const [tab, setTab] = useState(0);
  const [prepReview, setPrepReview] = useState(null);
  const leadId = linkedLead?._id || linkedLead?.id || quote?.leadId;

  const handleReviewChange = useCallback((review) => {
    setPrepReview(review);
  }, []);

  if (!quote) return null;

  return (
    <Box data-testid="quote-detail-workspace">
      <QuoteCustomerOfferCard quote={quote} />

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <QuoteNextActionChip quote={quote} />
          {quote.referenceNumber && (
            <Typography variant="caption" color="text.secondary">
              Ref {quote.referenceNumber}
            </Typography>
          )}
        </Box>
        {leadId && onOpenLead && (
          <Button size="small" variant="text" onClick={() => onOpenLead(leadId)}>
            Open Lead · Fulfilment
          </Button>
        )}
      </Box>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 0.5,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 42,
            },
          }}
        >
          <Tab label="Quote" />
          <Tab label="Fulfilment Plan (Internal)" />
          <Tab label="Customer History" />
          <Tab label="Activity Timeline" />
        </Tabs>
      </Box>

      <TabPanel value={tab} index={0}>
        <QuoteFinanceStrip quote={quote} review={prepReview} />

        <Grid container spacing={2.5} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <Box
              sx={{
                position: { lg: "sticky" },
                top: { lg: 16 },
                alignSelf: "flex-start",
              }}
            >
              <QuoteCustomerLineItems
                quote={quote}
                onPreviewPdf={onPreviewPdf}
                pdfReady={pdfReady}
                pdfBusy={pdfBusy}
              />
              <QuoteCustomerPreview quote={quote} />
            </Box>
          </Grid>
          <Grid item xs={12} lg={4}>
            <QuoteAutomatedPreparationReviewPanel
              quote={quote}
              onApproved={onPreparationApproved}
              onRevisionCreated={onRevisionCreated}
              onReviewChange={handleReviewChange}
            />
            <QuoteInternalSummary
              quote={quote}
              showExplanationTree={false}
              hideFinance
            />
            <QuoteDiagnosticsPanel quote={quote} />
            <QuoteTimeline quote={quote} />
            <Card
              elevation={0}
              sx={{
                mb: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }} mb={1}>
                  Customer
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {quote.customerName || "Unknown"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {quote.customerPhone || "No phone"}
                </Typography>
                {quote.customerEmail && (
                  <Typography variant="body2" color="text.secondary">
                    {quote.customerEmail}
                  </Typography>
                )}
                {quote.notes && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      Internal notes
                    </Typography>
                    <Typography variant="body2">{quote.notes}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
              Fulfilment Plan
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Provider offers, truck selection, and internal costing belong here.
              Phase 1 keeps that work on the Lead until the Fulfilment Plan
              object lands.
            </Typography>
            <QuoteInternalSummary quote={quote} hideFinance />
            {leadId && onOpenLead && (
              <Button variant="contained" onClick={() => onOpenLead(leadId)}>
                Manage fulfilment on Lead
              </Button>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            mb: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>
              Customer history
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Notes and prior interactions for this customer.
            </Typography>
            {customerNotesSlot || (
              <Typography variant="body2" color="text.secondary">
                No customer notes panel available.
              </Typography>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <QuoteTimeline quote={quote} />
          </Grid>
          <Grid item xs={12} md={5}>
            {opsToolsSlot}
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default QuoteDetailWorkspace;
